import { useLayoutEffect, type ReactNode } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { I18nProvider } from '@mosaferati/i18n';
import { ThemeProvider, ToastProvider } from '@mosaferati/ui';
import { MotionConfig } from 'motion/react';
import { queryClient } from './query-client.ts';
import { usePrefs } from './prefs-store.ts';
import { createIdbPersister } from './persister.ts';

const persister = createIdbPersister();
// Only persist read-only, cheap-to-restore resources; never auth/me or streams.
const PERSIST_PREFIXES = ['cities', 'places', 'hotels', 'trips', 'segments', 'notifications', 'stations', 'categories', 'reviews'];

export function Providers({ children }: { children: ReactNode }) {
  const locale = usePrefs((s) => s.locale);
  const digits = usePrefs((s) => s.digits);
  const calendar = usePrefs((s) => s.calendar);
  useLayoutEffect(() => {
    document.documentElement.dataset.digits = digits;
  }, [digits]);
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 7 * 24 * 3600_000,
        buster: __APP_VERSION__,
        dehydrateOptions: { shouldDehydrateQuery: (qq) => qq.state.status === 'success' && PERSIST_PREFIXES.includes(String(qq.queryKey[0])) },
      }}
      // restored data is only a first paint: refetch whatever the screen is showing (the throttled persister may lag mutations)
      onSuccess={() => {
        void queryClient.invalidateQueries();
      }}
    >
      <ThemeProvider>
        <I18nProvider prefs={{ locale, digits, calendar }}>
          <MotionConfig reducedMotion="user">
            <ToastProvider>{children}</ToastProvider>
          </MotionConfig>
        </I18nProvider>
      </ThemeProvider>
    </PersistQueryClientProvider>
  );
}
